/**
 * Ordre total des lignes du roadbook — EF-CAL-006.
 *
 * Ce départage garantit un ordre total : deux lignes ne peuvent jamais rester
 * à égalité, et l'ordre est identique avant et après un cycle d'exportation et
 * d'importation (CA-023).
 */

import type { Passage, Poi, PoiId } from '../core/model';

/** Référence à une ligne de POI : un passage d'un POI donné. */
export interface PoiLineRef {
  readonly poiId: PoiId;
  /** Rang du passage, à partir de 1. */
  readonly passageIndex: number;
  readonly progressionMeters: number;
  readonly distanceToRouteMeters: number;
}

/**
 * Rang de type de ligne à progression égale :
 * `Départ < zone manquante < ligne de POI < Pause < Sommeil < Arrivée`.
 *
 * La zone de recherche manquante de EF-RBK-013 n'est pas nommée par l'ordre de
 * EF-CAL-006 : elle marque le début d'un intervalle et précède donc les POI
 * situés à la même progression.
 */
export const LINE_TYPE_RANK = {
  start: 0,
  missingRange: 1,
  poi: 2,
  pause: 3,
  sleep: 4,
  finish: 5,
} as const;

/**
 * Comparaison lexicographique stable et indépendante de la langue du système.
 * L'ordre est celui des points de code, jamais celui d'une collation locale,
 * qui varierait d'un appareil à l'autre et briserait CA-023.
 */
export function compareCodePoints(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Départage entre deux lignes de POI à progression égale. */
export function comparePoiLines(
  left: PoiLineRef,
  right: PoiLineRef,
  pois: ReadonlyMap<PoiId, Poi>,
): number {
  if (left.progressionMeters !== right.progressionMeters) {
    return left.progressionMeters - right.progressionMeters;
  }

  // 1 — distance croissante à la trace du passage.
  if (left.distanceToRouteMeters !== right.distanceToRouteMeters) {
    return left.distanceToRouteMeters - right.distanceToRouteMeters;
  }

  const a = pois.get(left.poiId);
  const b = pois.get(right.poiId);
  if (a === undefined || b === undefined) return compareCodePoints(left.poiId, right.poiId);

  // 2 — nom, les POI sans nom étant placés après les POI nommés.
  if (a.name !== b.name) {
    if (a.name === null) return 1;
    if (b.name === null) return -1;
    const byName = compareCodePoints(a.name, b.name);
    if (byName !== 0) return byName;
  }

  // 3 — origine, les POI automatiques avant les POI manuels.
  if (a.origin !== b.origin) return a.origin === 'osm' ? -1 : 1;

  // 4 — couple (type OSM, identifiant OSM) croissant.
  if (a.osm !== null && b.osm !== null) {
    const byType = compareCodePoints(a.osm.type, b.osm.type);
    if (byType !== 0) return byType;
    if (a.osm.id !== b.osm.id) return a.osm.id - b.osm.id;
  }

  // 5 — identifiant interne croissant.
  const byId = compareCodePoints(a.id, b.id);
  if (byId !== 0) return byId;

  // 6 — rang du passage croissant.
  return left.passageIndex - right.passageIndex;
}

function toRef(poi: Poi, passage: Passage): PoiLineRef {
  return {
    poiId: poi.id,
    passageIndex: passage.index,
    progressionMeters: passage.progressionMeters,
    distanceToRouteMeters: passage.distanceToRouteMeters,
  };
}

/**
 * Toutes les lignes de POI, triées selon l'ordre total.
 * Un POI possédant N passages produit N lignes.
 */
export function orderedPoiLines(
  pois: ReadonlyMap<PoiId, Poi>,
  keep: (poi: Poi) => boolean = (): boolean => true,
): readonly PoiLineRef[] {
  const refs: PoiLineRef[] = [];
  for (const poi of pois.values()) {
    if (!keep(poi)) continue;
    for (const passage of poi.passages) refs.push(toRef(poi, passage));
  }
  refs.sort((left, right) => comparePoiLines(left, right, pois));
  return refs;
}
